import { useState, useEffect } from "react";
import { Loader2, Plus, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState, StatCard } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { formatGs, formatDate } from "@/lib/format";

const CATEGORIES = ["Proveedor de seguidores", "Tráfico pago", "Herramientas", "Tasas", "Servicios", "Otros"];
const today = new Date().toISOString().slice(0, 10);
const EMPTY = { category: "Proveedor de seguidores", date: today, amount: 0, description: "" };

const Costs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => { setLoading(true); api.get("/admin/costs").then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.amount) return toast.error("Ingresá el monto");
    try { await api.post("/admin/costs", form); toast.success("Registrado"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/costs/${id}`); load(); };

  const total = items.reduce((a, b) => a + b.amount, 0);

  return (
    <div>
      <PageHeader title="Costos operacionales" subtitle="Registrá otros costos de la operación"
        action={<Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-cost-btn"><Plus className="h-4 w-4 mr-1" /> Nuevo costo</Button>} />

      <div className="grid grid-cols-2 gap-3 mb-5">
        <StatCard label="Costos totales" value={formatGs(total)} icon={Wallet} accent="rose" />
        <StatCard label="Registros" value={items.length} icon={Wallet} accent="slate" />
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : items.length === 0 ? <EmptyState icon={Wallet} text="Sin costos registrados." />
        : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {items.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-4" data-testid={`cost-${c.id}`}>
                <div>
                  <p className="font-medium text-slate-900">{c.category}</p>
                  <p className="text-xs text-slate-400">{formatDate(c.date)} {c.description && `· ${c.description}`}</p>
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-slate-900">{formatGs(c.amount)}</p>
                  <button onClick={() => del(c.id)} className="text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuevo costo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="cost-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-lg mt-1" /></div>
            <div><Label>Monto (₲)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="rounded-lg mt-1" data-testid="cost-amount" /></div>
            <div><Label>Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-lg mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={save} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-cost">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Costs;
